import { useEffect, useState, useMemo } from "react"
import useProducts from "./useProducts"

const usePurchaseProductable = (defaultProducts = [], searchQuery = "") => {
  const [purchaseItems, setPurchaseItems] = useState(defaultProducts)
  const [discount, setDiscount] = useState(0)
  const [subTotal, setSubTotal] = useState(0)
  const [vatAmt, setVatAmt] = useState(0)
  const [grandTotal, setGrandTotal] = useState(0)
  const [discountAmount, setDiscountAmount] = useState(0)
  const {
    products: allProducts,
    isLoading,
    currentPage,
  } = useProducts({ searchQuery })

  // Filter out products that are already in purchaseItems
  const products = useMemo(() => {
    return allProducts
  }, [allProducts])

  useEffect(() => {
    handleTotals()
  }, [purchaseItems, discount])

  const totalDistinctProducts = useMemo(() => {
    return purchaseItems.length
  }, [purchaseItems])

  const totalQuantity = useMemo(() => {
    return purchaseItems.reduce((total, item) => {
      return total + (item.quantity || 0)
    }, 0)
  }, [purchaseItems])

  const handleTotals = () => {
    const subT = purchaseItems.reduce((total, item) => {
      return total + (rowTotal(item) || 0)
    }, 0)

    const validDiscount = isNaN(discount) ? 0 : discount
    const discountAmt = (subT * validDiscount) / 100
    const totalAfterDiscount = subT - discountAmt
    const calculatedVatAmt = totalAfterDiscount * 0.13
    const newGrandTotal = totalAfterDiscount + calculatedVatAmt

    setSubTotal(subT.toFixed(2))
    setVatAmt(calculatedVatAmt.toFixed(2))
    setDiscountAmount(discountAmt.toFixed(2))
    setGrandTotal(newGrandTotal.toFixed(2))
  }

  const handleDiscountChange = (event) => {
    const value = parseFloat(event.target.value) || 0
    setDiscount(value)
  }
  const handleItemDiscountChange = (index, newValue) => {
    const updatedPurchaseItems = [...purchaseItems]
    updatedPurchaseItems[index].discount = Number(newValue)
    setPurchaseItems(updatedPurchaseItems)
  }

  const handleAddedPurchaseItem = (selected) => {
    setPurchaseItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.id === selected)
      if (existingItem) {
        return prevItems.map((item) =>
          item.id === selected ? { ...item, quantity: item.quantity + 1 } : item
        )
      }

      const productToAdd = allProducts.find(
        (product) => product.id === selected
      )
      if (!productToAdd) return prevItems

      return [...prevItems, { ...productToAdd, quantity: 1, discount }]
    })
  }

  const handleAddedPurchaseItemViaBarcode = (product) => {
    setPurchaseItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.id === product.id)

      if (existingItem) {
        return prevItems.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      }

      return [...prevItems, { ...product, quantity: 1, discount }]
    })
  }

  const handleRemovePurchaseItem = (itemToRemove) => {
    setPurchaseItems((prevItems) =>
      prevItems.filter((item) => item.id !== itemToRemove.id)
    )
  }

  const handleQuantityChange = (index, newValue) => {
    const updatedPurchaseItems = [...purchaseItems]
    updatedPurchaseItems[index].quantity = Number(newValue)
    setPurchaseItems(updatedPurchaseItems)
  }
  const handlePriceChange = (index, newValue) => {
    const updatedPurchaseItems = [...purchaseItems]
    updatedPurchaseItems[index].cost_price = Number(newValue)
    setPurchaseItems(updatedPurchaseItems)
  }

  const handleReset = () => {
    setPurchaseItems(defaultProducts)
    setDiscount(0)
  }

  const rowTotal = (item) => {
    const total = item.quantity * (item.cost_price / 1.13)
    const totalAfterDiscount = total - (total * item.discount) / 100
    return totalAfterDiscount
  }

  return {
    products,
    purchaseItems,
    subTotal,
    vatAmt,
    grandTotal,
    discountAmount,
    discount,
    isLoading,
    rowTotal,
    handleAddedPurchaseItem,
    handleAddedPurchaseItemViaBarcode,
    handleRemovePurchaseItem,
    handleQuantityChange,
    handleDiscountChange,
    handleItemDiscountChange,
    handlePriceChange,
    handleReset,
    productCurrentPage: currentPage,
    totalDistinctProducts,
    totalQuantity,
  }
}

export default usePurchaseProductable
